<template>
  <div>
    <!-- Header Card -->
    <div class="card bg-primary text-white mb-4">
      <h5 class="card-header mb-0">{{ $t('Webportal.Actions.InquiryWizard.Step4.Title') }}</h5>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingCheckData" class="text-center my-5">
      <b-spinner variant="primary" :label="$t('Webportal.Loading')"></b-spinner>
      <p class="mt-3 text-muted">{{ $t('Webportal.Loading') }}...</p>
    </div>

    <!-- Main Content -->
    <div v-else-if="selectedQuestions.length > 0">
      <div class="d-flex justify-content-between">
        <div>{{ $t('Webportal.Actions.InquiryWizard.Step4.YourQuestions') }}</div>
        <div class="small text-muted">{{ $t('Webportal.Actions.InquiryWizard.Step4.QuestionProgress', { current: currentQuestionIndex + 1, total: selectedQuestions.length }) }}</div>
      </div>

      <!-- Question Tabs -->
      <div class="mb-4">
        <div class="small text-muted mb-2">{{ $t('Webportal.Actions.InquiryWizard.Step4.TabHint') }}</div>
        <div class="d-flex flex-wrap">
          <b-button
            v-for="(question, index) in selectedQuestions"
            :key="question.SurveyQuestionId"
            :variant="index === currentQuestionIndex ? 'primary' : 'outline-primary'"
            :class="['question-tab', 'mr-2', 'mb-2', { 'question-tab-incomplete': !isQuestionComplete(question.SurveyQuestionId) }]"
            @click="navigateToQuestion(index)"
          >
            {{ question.QuestionNumber }}
          </b-button>
        </div>
      </div>

      <!-- Current Question Card -->
      <b-card no-body v-if="currentQuestion" class="mb-4">
        <b-card-header class="bg-primary text-white">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="question-number-circle d-flex align-items-center justify-content-center mr-3">
                <strong>{{ currentQuestionIndex + 1 }}</strong>
              </div>
              <div>
                <div class="d-flex align-items-center">
                  <span class="font-weight-bold mr-2">{{ currentQuestion.QuestionNumber }}</span>
                  <b-badge variant="light">{{ currentQuestion.ClusterText }}</b-badge>
                </div>
                <p class="mb-0">{{ currentQuestion.QuestionText }}</p>
              </div>
            </div>
          </div>
        </b-card-header>

        <b-card-body>
          <!-- Antwort aus dem Erlebnisbericht -->
          <QuestionResult
            :result="currentQuestion.Result"
            :selected-answers="currentQuestion.SelectedAnswers"
            :comment="currentQuestion.CommentByShopper"
            class="mb-4"
          />

          <!-- Erwartung gemäß Leitfaden -->
          <CollapsibleInfo
            v-if="currentQuestion.GuidelineTitle || currentQuestion.GuidelineDescription"
            :title="$t('Webportal.Actions.InquiryWizard.GuidelineExpectation')"
            :sub-title="currentQuestion.GuidelineTitle"
            :description="currentQuestion.GuidelineDescription"
            variant="info"
            :show-toggle="true"
            :initial-collapsed="isGuidelineCollapsed(currentQuestion.SurveyQuestionId)"
            @toggle="handleGuidelineToggle(currentQuestion.SurveyQuestionId, $event)"
            class="mb-4"
          />

          <!-- Form: Ihre Antwort aus Sicht des Autohauses -->
          <div class="mb-4">
            <label class="font-weight-bold d-block mb-2">
              <span class="question-number-small d-inline-flex align-items-center justify-content-center mr-1">1</span>
              {{ $t('Webportal.Actions.InquiryWizard.Step4.YourAnswer') }}
              <span v-if="!currentQuestion.IsCommentPossible" class="text-danger">*</span>
            </label>
            <div v-if="currentQuestionHasChoices">
              <!-- Multiple Choice -->
              <div v-if="currentQuestion.IsMultipleChoice">
                <b-form-checkbox-group
                  class="w-100"
                  v-model="desiredAnswerIds"
                  :options="getAnswerOptions(currentQuestion)"
                  @change="onDesiredAnswersChange"
                  buttons
                  button-variant="outline-primary"
                />
              </div>
              <!-- Single Choice -->
              <div v-else>
                <b-form-radio-group
                  class="w-100"
                  v-model="desiredAnswerIds"
                  :options="getAnswerOptions(currentQuestion)"
                  @change="onDesiredAnswersChange"
                  buttons
                  button-variant="outline-primary"
                />
              </div>
              <div v-if="!currentQuestion.IsCommentPossible && currentQuestionComplaint.desiredAnswers.length === 0" class="small text-danger mt-2">
                {{ $t('Webportal.Actions.InquiryWizard.Step4.AnswerRequired') }}
              </div>
            </div>
            <div v-else class="text-muted small">{{ $t('Webportal.Actions.InquiryWizard.Step4.NoAnswerOptions') }}</div>
          </div>

          <!-- Form: Detaillierte Begründung -->
          <div class="mb-2">
            <label class="font-weight-bold d-block mb-2">
              <span class="question-number-small d-inline-flex align-items-center justify-content-center mr-1">2</span>
              {{ $t('Webportal.Actions.InquiryWizard.Step4.Justification') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-textarea
              v-model="localJustification"
              rows="5"
              :placeholder="$t('Webportal.Actions.InquiryWizard.Step4.JustificationPlaceholder')"
              @input="onJustificationInput"
            />
            <div class="d-flex align-items-center justify-content-between mt-2">
              <div class="small">
                <span :class="{ 'text-danger': justificationCharCount < 50 }">
                  {{ justificationValidationMessage }}
                </span>
              </div>
              <div class="small text-muted">{{ $t('Webportal.Actions.InquiryWizard.CharCount', { count: justificationCharCount }) }}</div>
            </div>
          </div>

          <!-- AI Analysis Result -->
          <div v-if="aiAnalysisLoading" class="text-center mb-4">
            <b-spinner small variant="secondary" />
            <small class="text-muted ml-2">{{ $t('Webportal.Actions.InquiryWizard.Step4.AiLoading') }}</small>
          </div>
          <div v-else-if="currentAiAnalysis" class="mb-4">
            <div
              :class="[
                'alert',
                !currentAiAnalysis.relevance ? 'alert-danger'
                  : currentAiAnalysis.justified ? 'alert-success'
                  : 'alert-warning'
              ]"
            >
              <div class="d-flex align-items-center mb-3">
                <font-awesome-icon
                  :icon="['fal', !currentAiAnalysis.relevance ? 'times-circle' : currentAiAnalysis.justified ? 'check-circle' : 'exclamation-circle']"
                  class="mr-2"
                />
                <strong>{{
                  !currentAiAnalysis.relevance ? $t('Webportal.Actions.InquiryWizard.Step4.AiNotRelevant')
                    : currentAiAnalysis.justified ? $t('Webportal.Actions.InquiryWizard.Step4.AiJustified')
                    : $t('Webportal.Actions.InquiryWizard.Step4.AiNotJustified')
                }}</strong>
              </div>
              <div class="d-flex justify-content-between small mb-1">
                <span>{{ $t('Webportal.Actions.InquiryWizard.Step4.AiScore') }}</span>
                <span>{{ currentAiAnalysis.score }}%</span>
              </div>
              <b-progress
                :value="currentAiAnalysis.score"
                max="100"
                :variant="!currentAiAnalysis.relevance ? 'danger' : currentAiAnalysis.justified ? 'success' : 'warning'"
                height="8px"
                class="mb-3"
              />
              <div v-if="currentAiAnalysis.analysisPoints && currentAiAnalysis.analysisPoints.length > 0">
                <div class="font-weight-bold small mb-2">{{ $t('Webportal.Actions.InquiryWizard.Step4.AiAnalysisPoints') }}</div>
                <ul class="small mb-0 pl-4">
                  <li v-for="(point, i) in currentAiAnalysis.analysisPoints" :key="i" class="mb-1">{{ point }}</li>
                </ul>
              </div>
              <div v-if="currentAiAnalysis.improvementSuggestions && currentAiAnalysis.improvementSuggestions.length > 0 && !(currentAiAnalysis.relevance && currentAiAnalysis.justified && currentAiAnalysis.score >= 80)" class="mt-2">
                <div class="font-weight-bold small mb-2">{{ $t('Webportal.Actions.InquiryWizard.Step4.AiImprovements') }}</div>
                <ul class="small mb-0 pl-4">
                  <li v-for="(suggestion, i) in currentAiAnalysis.improvementSuggestions" :key="'s' + i" class="mb-1">{{ suggestion }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Form: Belege -->
          <div>
            <label class="font-weight-bold d-block mb-3">
              <span class="question-number-small d-inline-flex align-items-center justify-content-center mr-1">3</span>
              {{ $t('Webportal.Actions.InquiryWizard.Step4.Evidence') }}
              <span v-if="currentQuestion.IsAttachmentMandatory" class="text-danger">*</span>
            </label>

            <!-- Uploaded Files List -->
            <AttachmentList
              :attachments="currentQuestionComplaint.attachments"
              :editable="true"
              @attachment-deleted="handleAttachmentDeleted"
              @attachment-updated="handleAttachmentUpdated"
              class="mb-3"
            />

            <!-- Upload Drop Zone -->
            <AttachmentDropZone @attachments-added="handleAttachmentsAdded" />
          </div>
        </b-card-body>
      </b-card>

      <!-- Validation Alert -->
      <b-alert v-if="!isCurrentQuestionValid" show variant="danger" class="d-flex align-items-center">
        <font-awesome-icon :icon="['fal', 'exclamation-triangle']" class="mr-2" />
        <span>{{ $t('Webportal.Actions.InquiryWizard.Step4.RequiredFields') }}</span>
      </b-alert>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import ActionService from "@/services/ActionService";
import ScaleIndicator from "./ScaleIndicator.vue";
import QuestionResult from "./QuestionResult.vue";
import CollapsibleInfo from "./CollapsibleInfo.vue";
import AttachmentDropZone from "./AttachmentDropZone.vue";
import AttachmentList from "./AttachmentList.vue";
import _ from "lodash";

export default {
  name: "Step4AnswerQuestions",

  components: {
    ScaleIndicator,
    QuestionResult,
    CollapsibleInfo,
    AttachmentDropZone,
    AttachmentList,
  },

  data() {
    return {
      currentQuestionIndex: 0,
      isLoadingCheckData: false,
      checkData: null,
      desiredAnswerIds: [], // Temporary v-model for checkboxes/radios
      localJustification: "", // Local copy for immediate character count
      guidelineCollapseStates: {}, // Tracks collapse state per question ID
      aiAnalysisLoading: false,
      aiAnalysisCancelSource: null, // CancelTokenSource for the in-flight AI request
    };
  },

  computed: {
    ...mapGetters("inquiryWizard", [
      "selectedCheckId",
      "selectedQuestionIds",
      "getQuestionComplaint",
      "getQuestionAiAnalysis",
      "getQuestionHasChoices",
      "isQuestionComplete",
      "isQuestionAnswersValid",
      "isQuestionJustificationValid",
      "allQuestionsComplete",
    ]),

    selectedQuestions() {
      if (!this.checkData || !this.checkData.Questions) {
        return [];
      }

      // Map comments, guidelines, and answers to questions
      const questionsWithDetails = this.checkData.Questions.map((question) => {
        const comment = this.checkData.Comments?.find((c) =>
          question.IsCommentPossible
            ? c.QuestionNumber == question.QuestionNumber
            : question.QuestionNumber && c.QuestionNumber.startsWith(question.QuestionNumber)
        );

        const guideline = this.checkData.Guidelines?.find((g) => g.SurveyQuestionId === question.SurveyQuestionId);

        // Filter to show only selected answers (IsAnswerSelected === true)
        const selectedAnswers =
          this.checkData.Answers?.filter((a) => a.SurveyQuestionId === question.SurveyQuestionId && a.IsAnswerSelected === true) || [];

        return {
          ...question,
          CommentByShopper: comment ? comment.CommentByShopper : null,
          GuidelineTitle: guideline ? guideline.GuidelineTitle : null,
          GuidelineDescription: guideline ? guideline.GuidelineDescription : null,
          SelectedAnswers: selectedAnswers,
        };
      });

      // Filter to only selected questions from Step 3
      return questionsWithDetails.filter((q) => this.selectedQuestionIds.includes(q.SurveyQuestionId));
    },

    currentQuestion() {
      return this.selectedQuestions[this.currentQuestionIndex] || null;
    },

    currentQuestionAnswers() {
      if (!this.currentQuestion) return [];
      return this.getAllAnswersForQuestion(this.currentQuestion);
    },

    currentQuestionHasChoices() {
      if (!this.currentQuestion) return false;
      return this.getQuestionHasChoices(this.currentQuestion.SurveyQuestionId);
    },

    currentQuestionComplaint() {
      if (!this.currentQuestion) {
        return { desiredAnswers: [], justification: "", attachments: [] };
      }
      return this.getQuestionComplaint(this.currentQuestion.SurveyQuestionId);
    },

    currentAiAnalysis() {
      if (!this.currentQuestion) return null;
      return this.getQuestionAiAnalysis(this.currentQuestion.SurveyQuestionId);
    },

    isCurrentQuestionValid() {
      if (!this.currentQuestion) return false;
      return this.isQuestionComplete(this.currentQuestion.SurveyQuestionId);
    },

    justificationCharCount() {
      return (this.localJustification || "").length;
    },

    justificationValidationMessage() {
      if (this.justificationCharCount >= 50) {
        return "";
      }
      return this.$t('Webportal.Actions.InquiryWizard.Step4.JustificationMinLength');
    },
  },

  watch: {
    currentQuestionIndex() {
      this.syncDesiredAnswers();
      this.emitWizardButtonUpdates();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    allQuestionsComplete() {
      this.emitWizardButtonUpdates();
    },

    "selectedQuestions.length"() {
      this.emitWizardButtonUpdates();
    },
  },

  created() {
    this.loadCheckData();
  },

  methods: {
    ...mapActions("inquiryWizard", [
      "setQuestionDesiredAnswers",
      "setQuestionJustification",
      "addQuestionAttachment",
      "updateQuestionAttachment",
      "removeQuestionAttachment",
      "setQuestionAttachmentMandatory",
      "setQuestionIsCommentPossible",
      "setQuestionHasChoices",
      "setQuestionAiAnalysis",
    ]),

    async loadCheckData() {
      if (!this.selectedCheckId) {
        this.$router.push("/action/inquiry-wizard");
        return;
      }

      this.isLoadingCheckData = true;
      try {
        const response = await ActionService.getSurveyInstanceData(this.selectedCheckId, true, true, true);
        this.checkData = response.data;

        // Initialize Vuex state for all selected questions if not already initialized
        this.selectedQuestions.forEach((question) => {
          const complaint = this.getQuestionComplaint(question.SurveyQuestionId);
          if (!complaint.desiredAnswers) {
            this.setQuestionDesiredAnswers({
              questionId: question.SurveyQuestionId,
              desiredAnswers: [],
            });
          }
          // Store IsAttachmentMandatory from backend data
          this.setQuestionAttachmentMandatory({
            questionId: question.SurveyQuestionId,
            isAttachmentMandatory: question.IsAttachmentMandatory ?? false,
          });
          // Store IsCommentPossible from backend data
          this.setQuestionIsCommentPossible({
            questionId: question.SurveyQuestionId,
            isCommentPossible: question.IsCommentPossible ?? false,
          });
          // Store whether this question has more than one answer option
          this.setQuestionHasChoices({
            questionId: question.SurveyQuestionId,
            hasChoices: this.getAllAnswersForQuestion(question).length > 1,
          });
        });

        // Sync desired answers for current question
        this.syncDesiredAnswers();

        // Emit initial button states
        this.$nextTick(() => {
          this.emitWizardButtonUpdates();
        });
      } catch (error) {
        console.error("Error loading check data:", error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step3.LoadError'), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
      } finally {
        this.isLoadingCheckData = false;
      }
    },

    navigateToQuestion(index) {
      this.currentQuestionIndex = index;
    },

    syncDesiredAnswers() {
      // Sync desiredAnswerIds and localJustification from Vuex store
      if (this.currentQuestion) {
        const complaint = this.getQuestionComplaint(this.currentQuestion.SurveyQuestionId);
        if (this.currentQuestion.IsMultipleChoice) {
          this.desiredAnswerIds = complaint.desiredAnswers.map((a) => a.ExternalSurveyAnswerId);
        } else {
          this.desiredAnswerIds = complaint.desiredAnswers.length > 0 ? complaint.desiredAnswers[0].ExternalSurveyAnswerId : null;
        }
        // Sync local justification with Vuex
        this.localJustification = complaint.justification || "";
      }
    },

    getAllAnswersForQuestion(question) {
      if (!this.checkData || !this.checkData.Answers) return [];
      return this.checkData.Answers.filter((a) => a.SurveyQuestionId === question.SurveyQuestionId);
    },

    getAnswerOptions(question) {
      const allAnswers = this.getAllAnswersForQuestion(question);
      return allAnswers.map((answer) => ({
        value: answer.ExternalSurveyAnswerId,
        text: answer.SurveyAnswerText,
      }));
    },

    onDesiredAnswersChange() {
      if (!this.currentQuestion) return;

      const allAnswers = this.currentQuestionAnswers;
      let selectedAnswers = [];

      if (this.currentQuestion.IsMultipleChoice) {
        // Multiple choice: desiredAnswerIds is an array
        selectedAnswers = this.desiredAnswerIds
          .map((answerId) => {
            const answer = allAnswers.find((a) => a.ExternalSurveyAnswerId === answerId);
            if (answer) {
              return {
                ExternalSurveyAnswerId: answer.ExternalSurveyAnswerId,
                Position: answer.Position,
                SurveyAnswerText: answer.SurveyAnswerText,
              };
            }
            return null;
          })
          .filter((a) => a !== null);
      } else {
        // Single choice: desiredAnswerIds is a single value
        if (this.desiredAnswerIds) {
          const answer = allAnswers.find((a) => a.ExternalSurveyAnswerId === this.desiredAnswerIds);
          if (answer) {
            selectedAnswers = [
              {
                ExternalSurveyAnswerId: answer.ExternalSurveyAnswerId,
                Position: answer.Position,
                SurveyAnswerText: answer.SurveyAnswerText,
              },
            ];
          }
        }
      }

      this.setQuestionDesiredAnswers({
        questionId: this.currentQuestion.SurveyQuestionId,
        desiredAnswers: selectedAnswers,
      });

      // Cancel any in-flight request immediately, then schedule a new one
      this.cancelAiAnalysis();
      this.requestAiAnalysis();
    },

    onJustificationInput(value) {
      if (this.currentQuestion) {
        this.setQuestionJustification({
          questionId: this.currentQuestion.SurveyQuestionId,
          justification: value,
        });
      }
      // Cancel any in-flight request immediately, then schedule a new one
      this.cancelAiAnalysis();
      this.requestAiAnalysis();
    },

    cancelAiAnalysis() {
      if (this.aiAnalysisCancelSource) {
        this.aiAnalysisCancelSource.cancel();
        this.aiAnalysisCancelSource = null;
      }
      this.aiAnalysisLoading = false;
    },

    /**
     * Debounced handler that calls the AI analysis endpoint 2 seconds after the
     * last answer selection or justification change.
     *
     * Reads exclusively from the Vuex store so validation reflects the committed
     * state rather than transient local UI values. The store is updated synchronously
     * by onJustificationInput and onDesiredAnswersChange, so it is always current
     * by the time this timer fires.
     *
     * Only fires when:
     *  - The store justification is non-empty / non-whitespace.
     *  - For choice questions (single/multiple): at least one desired answer is
     *    present in the store complaint.
     */
    requestAiAnalysis: _.debounce(function () {
      if (!this.currentQuestion || !this.selectedCheckId) return;

      const questionId = this.currentQuestion.SurveyQuestionId;

      // Use store validators — both must pass before calling the AI
      if (!this.isQuestionJustificationValid(questionId)) return;
      if (this.currentQuestionHasChoices && !this.isQuestionAnswersValid(questionId)) return;

      // Derive ExternalSurveyAnswerIds from the store objects
      const complaint = this.getQuestionComplaint(questionId);
      const desiredAnswerIds = (complaint.desiredAnswers || []).map((a) => a.ExternalSurveyAnswerId);

      this.aiAnalysisCancelSource = axios.CancelToken.source();
      this.aiAnalysisLoading = true;

      ActionService.getAiAnalysis(
        this.selectedCheckId,
        this.currentQuestion.SurveyQuestionId,
        desiredAnswerIds,
        complaint.justification,
        undefined,
        this.aiAnalysisCancelSource
      ).then((response) => {
        this.setQuestionAiAnalysis({ questionId, result: response.data });
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('AI analysis request failed:', error);
        }
      }).finally(() => {
        this.aiAnalysisLoading = false;
      });
    }, 2000),

    async handleAttachmentsAdded(attachments) {
      if (!this.currentQuestion || attachments.length === 0) return;

      try {
        for (const attachment of attachments) {
          await this.addQuestionAttachment({
            questionId: this.currentQuestion.SurveyQuestionId,
            attachment,
          });
        }
      } catch (error) {
        this.$bvToast.toast(error.message || this.$t('Webportal.Actions.InquiryWizard.Step4.AttachmentError'), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
      }
    },

    handleAttachmentDeleted(attachmentIndex) {
      if (!this.currentQuestion) return;

      this.removeQuestionAttachment({
        questionId: this.currentQuestion.SurveyQuestionId,
        attachmentIndex,
      });
    },

    handleAttachmentUpdated({ index, attachment }) {
      if (!this.currentQuestion) return;

      this.updateQuestionAttachment({
        questionId: this.currentQuestion.SurveyQuestionId,
        attachmentIndex: index,
        attachment,
      });
    },

    emitWizardButtonUpdates() {
      // Emit events to wizard container to update button labels and states
      const isFirstQuestion = this.currentQuestionIndex === 0;
      const isLastQuestion = this.currentQuestionIndex === this.selectedQuestions.length - 1;

      this.$emit("update-back-button-label", isFirstQuestion ? this.$t('Webportal.Actions.InquiryWizard.Landing.BackToSelection') : this.$t('Webportal.Actions.InquiryWizard.Step4.PrevQuestion'));
      this.$emit("update-next-button-label", isLastQuestion ? this.$t('Webportal.Actions.InquiryWizard.Step4.ToReview') : this.$t('Webportal.Actions.InquiryWizard.Step4.NextQuestion'));
      // Only disable when on last question and trying to go to Step 5
      this.$emit("update-next-button-disabled", isLastQuestion && !this.allQuestionsComplete);
    },

    handleWizardBackClick() {
      if (this.currentQuestionIndex === 0) {
        // Allow default back navigation to Step 3
        return true;
      } else {
        // Navigate to previous question
        this.currentQuestionIndex--;
        return false; // Prevent wizard navigation
      }
    },

    handleWizardNextClick() {
      const isLastQuestion = this.currentQuestionIndex === this.selectedQuestions.length - 1;

      if (isLastQuestion && this.allQuestionsComplete) {
        // Allow default next navigation to Step 5 only when all questions complete
        return true;
      } else if (!isLastQuestion) {
        // Always allow navigation to next question within Step 4
        this.currentQuestionIndex++;
        return false; // Prevent wizard navigation
      }
      // On last question but not all complete - stay on current question
      return false;
    },

    isGuidelineCollapsed(questionId) {
      // Default to collapsed (true) if not set
      return this.guidelineCollapseStates[questionId] !== false;
    },

    handleGuidelineToggle(questionId, isCollapsed) {
      this.$set(this.guidelineCollapseStates, questionId, isCollapsed);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .btn-group {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.question-tab {
  min-width: 80px;

  &.question-tab-incomplete {
    border-color: var(--danger-20) !important;
    box-shadow: 0 0 0 2px var(--danger-20) !important;
  }
}

.question-number-circle {
  border-radius: 50%;
  font-size: 1.25rem;
  width: 40px;
  height: 40px;
  background-color: var(--primary-20);
  color: white;
}

.question-number-small {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--dark);
  color: white;
  font-size: 0.875rem;
  font-weight: bold;
}
</style>
